/** Shared helpers for ChesiSite shop pages — keeps image URLs and pricing consistent. */

export function getApiOrigin() {
  const base =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://chesiwms-production-1eb5.up.railway.app/api'
      : 'http://localhost:5000/api');
  return base.replace(/\/?api\/?$/i, '');
}

export function resolveImageUrl(value, fallbackText = 'Product') {
  if (!value) {
    return `https://via.placeholder.com/400x400?text=${encodeURIComponent(fallbackText)}`;
  }
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/assets/')) return value;
  const origin = getApiOrigin();
  return `${origin}${value.startsWith('/') ? '' : '/'}${value}`;
}

export function formatPrice(value, { showRequestPrice = true } = {}) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    return showRequestPrice ? 'Request Price' : '';
  }
  return `KES ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

export function getEffectivePrice(product) {
  const price = Number(product?.price || 0);
  const discount = Number(product?.discount || 0);
  if (discount > 0 && discount < price) return discount;
  return price;
}

export function isInStock(product) {
  return Number(product?.stock_quantity || 0) > 0;
}

export function getProductUrl(product) {
  const key = product.slug || product.id;
  return `/shop/product/${key}`;
}

export function mapApiProductToCard(p) {
  const image = resolveImageUrl(p.primary_image_url, p.sku || p.name || 'Product');
  const listPrice = Number(p?.price || 0);
  const discount = Number(p?.discount || 0);
  const hasDiscount = discount > 0 && discount < listPrice;
  const priceNum = hasDiscount ? discount : listPrice;
  return {
    id: p.id,
    slug: p.slug || null,
    name: p.name,
    sku: p.sku || '—',
    brandName: p.brand_name || null,
    brandSlug: p.brand_slug || null,
    price: formatPrice(priceNum),
    priceRaw: priceNum,
    originalPrice: hasDiscount ? formatPrice(listPrice) : null,
    hasDiscount,
    image,
    description: p.short_description || p.long_description || '',
    longDescription: p.long_description || p.short_description || '',
    categoryName: p.category_name || null,
    categorySlug: p.category_slug || null,
    stockQuantity: Number(p.stock_quantity || 0),
    inStock: isInStock(p),
    isFeatured: Boolean(p.is_featured),
    url: getProductUrl(p)
  };
}

const RECENTLY_VIEWED_KEY = 'chesi_recently_viewed';
const MAX_RECENT = 12;

export function getRecentlyViewed() {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addRecentlyViewed(product) {
  if (!product?.id) return;
  const entry = {
    id: product.id,
    slug: product.slug,
    name: product.name,
    sku: product.sku,
    price: product.price,
    priceRaw: getEffectivePrice(product),
    primary_image_url: product.primary_image_url || product.image,
    stock_quantity: product.stock_quantity,
    brand_name: product.brand_name,
    category_slug: product.category_slug
  };
  const existing = getRecentlyViewed().filter((p) => p.id !== entry.id);
  const next = [entry, ...existing].slice(0, MAX_RECENT);
  localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
}

const RECENT_SEARCHES_KEY = 'chesi_recent_searches';
const MAX_RECENT_SEARCHES = 8;

export function getRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addRecentSearch(term) {
  const q = String(term || '').trim();
  if (!q) return;
  const existing = getRecentSearches().filter((s) => s.toLowerCase() !== q.toLowerCase());
  const next = [q, ...existing].slice(0, MAX_RECENT_SEARCHES);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
}
